/**
 * The kitchen and the war room (docs/refactor/29): two more areas of the office, drawn with the
 * hall's parts — the same wall, floor, light, windows and people — around their own furniture.
 *
 * Who stands here is whoever walked their character here (right click on it, "Ir para…"). Their
 * agents keep working in the hall, so nothing here says what anybody is working on: a name, the
 * character, and what they say in the room's chat. No desks, no counts per person (§43).
 *
 * The room grows with the crowd like the hall (layout.ts, roomHeightFor).
 */

import { memo, useMemo, useRef } from 'react'
import { computeFloor, horizonFor, roomHeightFor, type FloorSize, type Station } from './layout'
import { CityWindow, Plant, SceneDefs } from './Scene'
import { Chair, DeveloperFigure, lookFor } from './Figures'
import { AgentAvatar } from './AgentFigures'
import { agentLookFor, parsePreference } from './agentLooks'
import { useScenery } from './scenery'
import { useOnScreen } from './useOnScreen'
import { AREA_STRIP } from './office'
import type { DeveloperEntity } from './store'
import type { RoomArea } from '../lib/types'

const INK = '#1a1f2c'

export interface OfficeAreaProps {
  area: Exclude<RoomArea, 'hall'>
  /** The measured design size; the room grows past it for a crowd. */
  size: FloorSize
  people: DeveloperEntity[]
  title: string
  /** "3 pessoas", under the title. */
  peopleLabel: string
  /** Said on the floor while nobody is here. */
  emptyHint: string
  /** The war room's screen: agents at work in the whole office right now, and what to call them. */
  screen?: { value: number; label: string }
  selectedDeveloperId: string | null
  speech?: Map<string, string>
  reducedMotion: boolean
  onSelect: (id: string | null) => void
}

export const OfficeArea = memo(function OfficeArea(props: OfficeAreaProps) {
  const { area, size, people, title, peopleLabel, emptyHint, screen, selectedDeveloperId, speech, reducedMotion, onSelect } = props
  const svgRef = useRef<SVGSVGElement>(null)
  const onScreen = useOnScreen(svgRef)
  const { time } = useScenery()

  const room = useMemo<FloorSize>(() => ({ width: size.width, height: roomHeightFor(people.length, size, AREA_STRIP) }), [people.length, size])
  const floor = useMemo(() => computeFloor(people.map((p) => p.id), room, AREA_STRIP), [people, room])
  const byId = useMemo(() => new Map(people.map((p) => [p.id, p])), [people])
  const horizon = horizonFor(room)

  return (
    <svg
      ref={svgRef}
      className={`bh-scene office-scene office-scene--${area} scenery--${time}${reducedMotion || !onScreen ? ' bh-scene--still' : ''}`}
      viewBox={`0 0 ${room.width} ${room.height}`}
      preserveAspectRatio="xMidYMid meet"
      role="img"
      aria-label={`${title} · ${peopleLabel}`}
      data-office-area={area}
      onClick={() => onSelect(null)}
    >
      <SceneDefs />
      <Shell size={room} />
      {area === 'kitchen' ? <Kitchen size={room} /> : <WarRoom size={room} screen={screen} />}

      {/* The area's name on the wall, like the hall's zones. */}
      <g className="office-scene__sign" aria-hidden="true">
        <text x={room.width / 2} y={horizon - 18} textAnchor="middle" className="office-scene__title">{title}</text>
        <text x={room.width / 2} y={horizon - 2} textAnchor="middle" className="office-scene__count">{peopleLabel}</text>
      </g>

      {floor.stations.map((station) => {
        const person = byId.get(station.developerId)
        if (!person) return null
        return (
          <Person
            key={person.id}
            station={station}
            person={person}
            selected={selectedDeveloperId === person.id}
            speech={speech?.get(person.id)}
            onSelect={onSelect}
          />
        )
      })}

      {people.length === 0 && (
        <text x={room.width / 2} y={horizon + (room.height - horizon) * 0.78} textAnchor="middle" className="office-scene__empty">
          {emptyHint}
        </text>
      )}
    </svg>
  )
})

/** Somebody standing in the area: their character, their name, and what they just said. */
function Person({
  station,
  person,
  selected,
  speech,
  onSelect,
}: {
  station: Station
  person: DeveloperEntity
  selected: boolean
  speech: string | undefined
  onSelect: (id: string | null) => void
}) {
  // An agent with a character of its own is drawn as an agent, never as a person (docs/refactor/25).
  const agentLook = person.isAgent ? agentLookFor(person.id, parsePreference(person.agentLooks)) : null

  return (
    <g
      className={`bh-station office-person${selected ? ' bh-station--selected' : ''}`}
      data-drop-developer={person.id}
      transform={`translate(${station.x} ${station.y}) scale(${station.scale})`}
      role="button"
      tabIndex={0}
      aria-label={person.name}
      onClick={(event) => {
        event.stopPropagation()
        onSelect(selected ? null : person.id)
      }}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault()
          onSelect(selected ? null : person.id)
        }
      }}
    >
      {selected && <ellipse className="bh-station__ring" cx="0" cy="2" rx="40" ry="10" />}
      {agentLook ? (
        <g transform="translate(0 -52) scale(2.3)">
          <AgentAvatar look={agentLook} state="idle" title={person.name} />
        </g>
      ) : (
        <DeveloperFigure look={lookFor(person.id, person.avatar)} pose="working" swinging={false} />
      )}

      {/* The name at a fixed size whatever the crowd, and over it what they just said in the room. */}
      <g transform={`translate(0 -150) scale(${station.cardScale / station.scale})`}>
        <rect className="office-person__tag" x={-64} y={-16} width={128} height={22} rx={11} />
        <text className="office-person__name" x="0" y="-1" textAnchor="middle">{truncate(person.name, 18)}</text>
        {speech && <text className="office-person__speech" x="0" y="-26" textAnchor="middle">{truncate(speech, 40)}</text>}
      </g>
    </g>
  )
}

/** The wall and the floor every area shares with the hall. */
function Shell({ size }: { size: FloorSize }) {
  const horizon = horizonFor(size)
  const { width, height } = size
  return (
    <g aria-hidden="true">
      <rect x="0" y="0" width={width} height={horizon} fill="url(#bh-wall)" />
      <rect x="0" y={horizon - 10} width={width} height="10" fill="var(--bh-skirting)" />
      <rect x="0" y={horizon} width={width} height={height - horizon} fill="url(#bh-floor)" />
      <g stroke="var(--bh-line)" strokeWidth="1" opacity="0.4">
        {[0.15, 0.4, 0.72].map((t) => (
          <line key={t} x1="0" y1={horizon + (height - horizon) * t} x2={width} y2={horizon + (height - horizon) * t} />
        ))}
      </g>
    </g>
  )
}

/** A piece of furniture: a box with the room's ink outline, in units from where the wall meets the floor. */
function box(x: number, y: number, width: number, height: number, fill: string, rx = 3) {
  return <rect key={`${x},${y}`} x={x} y={y} width={width} height={height} rx={rx} fill={fill} stroke={INK} strokeWidth="1.4" />
}

/** Cabinets and a counter with the coffee machine, the fridge, a window, and a table with stools. */
function Kitchen({ size }: { size: FloorSize }) {
  const horizon = horizonFor(size)
  return (
    <g aria-hidden="true">
      <CityWindow x={650} y={30} width={170} height={Math.max(60, horizon - 110)} seed={7} />
      <g transform={`translate(0 ${horizon})`}>
        {box(90, -170, 380, 58, '#2E3A52')}
        {box(90, -52, 380, 60, '#36425C', 0)}
        {box(84, -58, 392, 9, '#C9D2DF', 2)}
        {box(372, -110, 58, 52, '#1C2333', 5)}
        {box(392, -74, 18, 15, '#F4F7FB', 2)}
        <path className="office-steam" d="M 398 -80 q -6 -10 0 -18 q 6 -8 0 -16" fill="none" stroke="rgba(255,255,255,0.45)" strokeWidth="2" />
        {box(500, -176, 86, 184, '#B7C2D3', 6)}
        {box(572, -150, 5, 70, '#6F7B90', 2)}
        <g transform={`translate(500 ${Math.min(150, (size.height - horizon) * 0.38)})`}>
          {box(-8, 0, 16, 36, '#4A3A2C', 0)}
          <ellipse rx="140" ry="30" fill="#8A6A4E" stroke={INK} strokeWidth="1.4" />
          {box(-180, 14, 22, 12, '#C2410C', 6)}
          {box(158, 14, 22, 12, '#C2410C', 6)}
        </g>
      </g>
      <Plant x={930} y={horizon + 14} scale={1.1} />
    </g>
  )
}

/** A big screen on the wall, a pin board, a window, and a long table with chairs. */
function WarRoom({ size, screen }: { size: FloorSize; screen?: { value: number; label: string } }) {
  const horizon = horizonFor(size)
  const screenHeight = Math.max(90, horizon - 90)
  return (
    <g aria-hidden="true">
      {/* The screen: the office's agents at work right now — the whole office, nobody singled out. */}
      {box(290, 28, 420, screenHeight, '#0E1C36', 8)}
      {screen && (
        <>
          <text x="500" y={28 + screenHeight * 0.45} textAnchor="middle" className="office-screen__value">{screen.value}</text>
          <text x="500" y={50 + screenHeight * 0.45} textAnchor="middle" className="office-screen__label">{screen.label}</text>
        </>
      )}
      {box(90, 56, 150, Math.max(70, horizon - 140), '#7A5A3E')}
      {box(106, 70, 40, 34, '#FFE066', 0)}
      {box(170, 90, 40, 34, '#74C0FC', 0)}
      <CityWindow x={790} y={30} width={120} height={Math.max(60, horizon - 110)} seed={19} />
      <Plant x={60} y={horizon + 14} scale={1} />
      <g transform={`translate(500 ${horizon + Math.min(140, (size.height - horizon) * 0.34)})`}>
        {[-150, -50, 50, 150].map((x) => (
          <g key={x} transform={`translate(${x} -30) scale(0.8)`}>
            <Chair />
          </g>
        ))}
        <path d="M -260 0 L 260 0 L 230 -40 L -230 -40 Z" fill="#3B4660" stroke={INK} strokeWidth="1.4" />
        {box(-260, 0, 520, 10, '#2A3346', 0)}
      </g>
    </g>
  )
}

function truncate(text: string, max: number): string {
  return text.length > max ? `${text.slice(0, max - 1)}…` : text
}
